// src/redux/stockGroups/stockGroupActions.js
import { createAsyncThunk } from '@reduxjs/toolkit';
import StockGroupAPI from './groupsToStockAPI';

// Normalize ticker to uppercase to avoid cache misses
const normTicker = (t) => (typeof t === 'string' ? t.toUpperCase() : t);

/**
 * Fetch groups for a stock ticker
 */
export const fetchGroupsForStock = createAsyncThunk(
  'stockGroups/fetchGroupsForStock',
  async (ticker, { rejectWithValue }) => {
    try {
      const data = await StockGroupAPI.getGroupsForStock(ticker);
      return { ticker: normTicker(ticker), data };
    } catch (err) {
      return rejectWithValue({ ticker: normTicker(ticker), message: err?.message || 'Failed to fetch groups for stock' });
    }
  }
);

/**
 * Fetch groups tracking a stock, ranked by how many people the current user
 * follows are already members -- powers the stock detail page's "Groups
 * discussing this stock" panel.
 */
export const fetchRecommendedGroupsForStock = createAsyncThunk(
  'stockGroups/fetchRecommendedGroupsForStock',
  async (ticker, { rejectWithValue }) => {
    try {
      const data = await StockGroupAPI.getRecommendedGroupsForStock(ticker);
      return { ticker: normTicker(ticker), data };
    } catch (err) {
      return rejectWithValue({ ticker: normTicker(ticker), message: err?.message || 'Failed to fetch recommended groups for stock' });
    }
  }
);

/**
 * Fetch stocks for a group
 */
export const fetchStocksForGroup = createAsyncThunk(
  'stockGroups/fetchStocksForGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      const data = await StockGroupAPI.getStocksForGroup(groupId);
      return { groupId, data };
    } catch (err) {
      return rejectWithValue({ groupId, message: err?.message || 'Failed to fetch stocks for group' });
    }
  }
);

/**
 * Associate stocks with a group
 */
export const associateStocksWithGroup = createAsyncThunk(
  'stockGroups/associateStocksWithGroup',
  async ({ groupId, stockIds }, { rejectWithValue }) => {
    try {
      const data = await StockGroupAPI.associateStocksWithGroup(groupId, stockIds);
      // backend may return updated association or a success object
      return { groupId, stockIds, data };
    } catch (err) {
      return rejectWithValue({ groupId, stockIds, message: err?.message || 'Failed to associate stocks with group' });
    }
  }
);

/**
 * Fetch paginated posts for a ticker
 */
export const fetchPostsForStock = createAsyncThunk(
  'stockGroups/fetchPostsForStock',
  async ({ ticker, skip = 0, limit = 10, sortBy = 'created_at' }, { rejectWithValue }) => {
    const T = normTicker(ticker);
    try {
      const data = await StockGroupAPI.getPostsForStock(T, skip, limit, sortBy);
      // Expect either { posts, has_more } or just an array
      return { ticker: T, skip, limit, sortBy, data };
    } catch (err) {
      return rejectWithValue({ ticker: T, skip, limit, sortBy, message: err?.message || 'Failed to fetch posts for stock' });
    }
  }
);
